import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Clock, House, ListChecks, X } from "lucide-react";
import type { ReactNode } from "react";
import { IconButton } from "./ui/IconButton";
import { PrimaryButton } from "./ui/Button";
import { cn } from "../lib/utils";

export interface PlanReadyScreenProps {
  spacesCount: number;
  tasksCreated: number;
  estimatedMinutes: number;
  onViewTasks: () => void;
  onClose: () => void;
}

function StatRow({
  icon,
  label,
  value,
  highlight = false,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-2xl px-4 py-3",
        highlight && "bg-surface"
      )}
    >
      <span className="text-muted-foreground" aria-hidden="true">
        {icon}
      </span>
      <dt className="flex-1 text-[15px] font-medium text-foreground">{label}</dt>
      <dd className="text-[15px] font-bold text-foreground tabular-nums">{value}</dd>
    </div>
  );
}

/** Hexagonal cyan badge with a checklist glyph. */
function PlanBadge({ tasksCreated }: { tasksCreated: number }) {
  return (
    <div className="relative w-36 h-36 mx-auto animate-pop-in" aria-hidden="true">
      <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-[0_16px_24px_rgba(14,165,233,0.35)]">
        <defs>
          <linearGradient id="plan-hex" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="100%" stopColor="#0284c7" />
          </linearGradient>
        </defs>
        <polygon
          points="50,4 90,27 90,73 50,96 10,73 10,27"
          fill="url(#plan-hex)"
          stroke="#7dd3fc"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <polygon
          points="50,16 80,33 80,67 50,84 20,67 20,33"
          fill="white"
          fillOpacity="0.12"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="w-14 h-14 rounded-2xl bg-white/90 flex items-center justify-center shadow-sm">
          <ListChecks className="w-8 h-8 text-sky-600" strokeWidth={2.5} />
        </span>
      </span>
      {tasksCreated > 0 && (
        <span className="absolute -top-1 -right-3 rounded-full bg-amber-300 px-2.5 py-1 text-base font-extrabold text-amber-950 shadow-md ring-4 ring-background">
          +{tasksCreated}
        </span>
      )}
    </div>
  );
}

/**
 * Full-screen success screen shown when an AI setup plan finishes.
 * Presentational only — the caller decides when to show it.
 */
export function PlanReadyScreen({
  spacesCount,
  tasksCreated,
  estimatedMinutes,
  onViewTasks,
  onClose,
}: PlanReadyScreenProps) {
  const primaryRef = useRef<HTMLButtonElement>(null);

  // Focus the main action and allow Escape to close
  useEffect(() => {
    primaryRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-ready-title"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 24 }}
      transition={{ type: "spring", damping: 28, stiffness: 260 }}
      className="fixed inset-0 z-[1100] bg-background flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]"
    >
      <div className="px-3 pt-3">
        <IconButton
          label="Close"
          variant="surface"
          icon={<X className="w-5 h-5" />}
          onClick={onClose}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-md mx-auto px-6 pt-4 pb-6 text-center">
          <PlanBadge tasksCreated={tasksCreated} />
          <h1
            id="plan-ready-title"
            className="mt-8 text-[28px] leading-tight font-extrabold tracking-tight text-foreground"
          >
            Your personalized cleaning plan is ready!
          </h1>
          <p className="mt-3 text-muted-foreground">
            We set up your spaces and created cleaning tasks tailored to your home.
          </p>

          <dl className="mt-8 space-y-1 text-left">
            <StatRow
              icon={<House className="w-5 h-5" />}
              label="Spaces set up"
              value={String(spacesCount)}
            />
            <StatRow
              icon={<ListChecks className="w-5 h-5" />}
              label="Tasks created"
              value={String(tasksCreated)}
              highlight
            />
            <StatRow
              icon={<Clock className="w-5 h-5" />}
              label="Estimated time"
              value={`~${estimatedMinutes} min`}
            />
          </dl>
        </div>
      </div>

      <div className="max-w-md w-full mx-auto px-6 pb-6">
        <PrimaryButton ref={primaryRef} onClick={onViewTasks}>
          View Tasks
        </PrimaryButton>
        <p className="mt-3 text-center text-sm text-muted-foreground">
          You can customize any task before getting started.
        </p>
      </div>
    </motion.div>
  );
}

export default PlanReadyScreen;
